/**
 *
 * @page 用户中心/我的消息/互动消息
 *
 **/

import BasePageWrap from '&/components/ResourceLib/BasePageWrap';
import { Pagination } from 'antd';
import { DeleteFilled, MailFilled } from '@ant-design/icons';
import { observer, inject } from 'mobx-react';
import BaseListWrap from '&/components/BaseListWrap';
import { useRouterTarget } from '&/utils/customHooks';
import useMessage from '@/utils/customHooks/useMessage';
import { SYNERGIA_TAB_STATUS } from '&/utils/baseMacro';
import './InteractiveMsg.scss';

function InteractiveMsg(props) {
    const { persenCenter } = props;
    const [
        title,
        msgList,
        loading,
        total,
        queryForm,
        onPageChange,
        deleteMsg,
        markAsRead,
    ] = useMessage(1, persenCenter.queryUnreadAmount, persenCenter.forceUpdate);

    const targetPage = useRouterTarget();

    const headerRight = (
        <button className="text-btn" onClick={() => markAsRead()}>
            {intl.get('userCenter.markAllRead')}
        </button>
    );

    const MessageItem = ({ data }) => {
        // 点击查看
        const targetTo = params => {
            if (params.url) {
                return (
                    <a
                        className="native-link"
                        target="_blank"
                        rel="noreferrer"
                        href={params.url}>
                        {intl.get('userCenter.clickHere')}
                    </a>
                );
            }

            let text = intl.get('userCenter.clickHere');
            const targetMethods = {
                order() {
                    // 会员开通/付款提醒/订单关闭：跳转订单详情
                    text = intl.get('base.viewDetail');
                    const { type } = params;
                    const FOLDERS = ['intelligent-design', 'cloud-services'];
                    targetPage({
                        pathname: `/user-center/${FOLDERS[type]}/order-detail`,
                        search: { type, id: params.orderCode },
                    });
                },
                software() {
                    // 会员续费: 跳转智能设计订单
                    text = intl.get('userCenter.renew');
                    targetPage({
                        pathname: '/user-center/intelligent-design',
                        search: {
                            subscribe: 1,
                        },
                    });
                },
                project() {
                    // 项目开通/项目续费:跳转项目详情
                    const { div } = params;
                    const FOLDERS = ['project-detail', 'scheme-detail'];
                    const TABS = [2, SYNERGIA_TAB_STATUS.INFO];
                    targetPage({
                        pathname: `/user-center/project-space/${
                            FOLDERS[div - 1]
                        }`,
                        search: {
                            id: params.projectId,
                            tab: TABS[div - 1],
                        },
                    });
                },
                projectMemberList() {
                    targetPage({
                        pathname: `/user-center/project-space/scheme-detail`,
                        search: {
                            id: params.projectId,
                            tab: SYNERGIA_TAB_STATUS.MEMBERS,
                        },
                    });
                },
                teamList() {
                    targetPage({
                        pathname: `/user-center/member-list`,
                    });
                },
                projectDrawing() {
                    //  项目施工图
                    targetPage({
                        pathname: '/user-center/project-space/scheme-detail',
                        search: {
                            id: params.project_id,
                            tab: SYNERGIA_TAB_STATUS.DRAWING,
                            drawing_id: params.drawing_id,
                        },
                    });
                },
                projectDrawingIndex() {
                    //  项目施工图首页
                    targetPage({
                        pathname: '/user-center/project-space/scheme-detail',
                        search: {
                            id: params.project_id,
                            tab: SYNERGIA_TAB_STATUS.DRAWING,
                        },
                    });
                },
                projectDetail() {
                    // 项目详情--进度计划列表
                    targetPage({
                        pathname: '/user-center/project-space/scheme-detail',
                        search: {
                            id: params.projectId,
                            tab: SYNERGIA_TAB_STATUS.PLANS,
                        },
                    });
                },
                planAdjunctDetail() {
                    // 施工计划详情---附件分享
                    targetPage({
                        pathname: '/user-center/project-space/scheme-detail',
                        search: {
                            id: params.projectId,
                            tab: SYNERGIA_TAB_STATUS.PLANS,
                            planId: params.planId,
                            adjunctId: params.adjunctId,
                        },
                    });
                },
                planDetail() {
                    // 施工计划详情
                    targetPage({
                        pathname: '/user-center/project-space/scheme-detail',
                        search: {
                            id: params.projectId,
                            tab: SYNERGIA_TAB_STATUS.PLANS,
                            planId: params.planId,
                        },
                    });
                },
                planCheckDetail() {
                    // 施工计划验收详情
                    targetPage({
                        pathname: '/user-center/project-space/scheme-detail',
                        search: {
                            id: params.projectId,
                            tab: SYNERGIA_TAB_STATUS.PLANS,
                            planCheckId: params.planId,
                        },
                    });
                },
                projectCheckDetail() {
                    // 项目验收详情
                    targetPage({
                        pathname: '/user-center/project-space/scheme-detail',
                        search: {
                            id: params.projectId,
                            tab: SYNERGIA_TAB_STATUS.PLANS,
                            projectCheck: 1,
                        },
                    });
                },
                planAdditional() {
                    // 施工计划的增项列表
                    targetPage({
                        pathname: '/user-center/project-space/scheme-detail',
                        search: {
                            id: params.projectId,
                            tab: SYNERGIA_TAB_STATUS.PLANS,
                            planId: params.planId,
                            detailTab: 1,
                        },
                    });
                },
                modelDetailWeb() {
                    targetPage(`/model-base/material-detail/${params.modelId}`);
                },
            };

            const fn = targetMethods[params.pageTarget] || null;

            return (
                fn && (
                    <a className="native-link" onClick={fn}>
                        {text}
                    </a>
                )
            );
        };

        const messageTarget = data => {
            if (!data.param1) return;
            try {
                const PARAMS = JSON.parse(data.param1);
                if (!PARAMS) return;
                return targetTo(PARAMS);
            } catch {}
        };

        return (
            <section
                className="message-item"
                onClick={() => markAsRead([data.id], data.status)}>
                <MailFilled
                    className={`read-icon ${data.status === 2 ? 'unred' : ''}`}
                />
                <div className="message-main">
                    <div className="message-content">
                        {data.content
                            .replace(intl.get('userCenter.clickHere'), '')
                            .replace(intl.get('userCenter.clickDetail'), '')}
                        {messageTarget(data)}
                    </div>
                    <div className="message-aside">
                        <div className="time">{data.createDate}</div>
                        <DeleteFilled
                            className="text-btn"
                            onClick={e => deleteMsg(e, data.id)}
                        />
                    </div>
                </div>
            </section>
        );
    };

    return (
        <BasePageWrap type="card" title={title} headerRight={headerRight}>
            <BaseListWrap isLoading={loading} isEmpty={!!msgList.length}>
                {msgList.map(e => (
                    <MessageItem key={e.id} data={e} />
                ))}
                <Pagination
                    size="small"
                    current={queryForm.page}
                    onChange={onPageChange}
                    pageSize={queryForm.limit}
                    total={total}
                    showSizeChanger={true}
                    showTotal={total => intl.get('base.total', { total })}
                />
            </BaseListWrap>
        </BasePageWrap>
    );
}

export default inject('persenCenter')(observer(InteractiveMsg));
